import { DateTime, FixedOffsetZone } from "luxon";
import { z } from "zod/v4-mini";

import { TandemConstants } from "@/lib/drivers/tandem/TandemConstants";
import { TandemCommand, TandemResponse } from "@/lib/drivers/tandem/TandemTypes";
import { DateUtils } from "@/utils/dateUtils";
import { cz } from "@/utils/zodUtils";

export type SleepSchedule = {
  enabled: boolean;
  days: {
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
  };

  startTime: string;
  endTime: string;
};

const dayFlags = {
  monday: 1,
  tuesday: 2,
  wednesday: 4,
  thursday: 8,
  friday: 16,
  saturday: 32,
  sunday: 64,
};

export type ControlIQPayload = {
  closed_loop_enabled: number;
  hypo_light_alert_enabled: number;
  total_daily_insulin: number;
  weight: number;
  weight_units: number;
  sleep_schedule: Uint8Array;
};

export type ControlIQData = {
  closedLoopEnabled: boolean;
  hypoLightAlertEnabled: boolean;
  totalDailyInsulin: number;
  weight: number;
  sleepSchedules: SleepSchedule[];
};

export const controlIQPayloadSchema = z.object({
  closed_loop_enabled: z.number(),
  hypo_light_alert_enabled: z.number(),
  total_daily_insulin: z.number(),
  weight: z.number(),
  weight_units: z.number(),
  sleep_schedule: cz.unsafe<Uint8Array>(),
});

const getSchedules = (data: Uint8Array): SleepSchedule[] => {
  const schedules: SleepSchedule[] = [];
  const dataView = new DataView(data.buffer);

  for (let i = 0; i < data.length; i += 6) {
    const enabled = dataView.getUint8(i);
    const activeDays = dataView.getUint8(i + 1);
    const startTime = dataView.getUint16(i + 2, true) * 60;
    const endTime = dataView.getUint16(i + 4, true) * 60;
    const days = {
      monday: !!(activeDays & dayFlags.monday),
      tuesday: !!(activeDays & dayFlags.tuesday),
      wednesday: !!(activeDays & dayFlags.wednesday),
      thursday: !!(activeDays & dayFlags.thursday),
      friday: !!(activeDays & dayFlags.friday),
      saturday: !!(activeDays & dayFlags.saturday),
      sunday: !!(activeDays & dayFlags.sunday),
    };

    schedules.push({
      enabled: !!enabled,
      days,
      startTime:
        DateTime.fromSeconds(startTime + TandemConstants.BASE_TIME, {
          zone: FixedOffsetZone.instance(DateUtils.tzOffset(DateTime.now())),
        }).toISOTime({
          suppressMilliseconds: true,
        }) ?? "",
      endTime:
        DateTime.fromSeconds(endTime + TandemConstants.BASE_TIME, {
          zone: FixedOffsetZone.instance(DateUtils.tzOffset(DateTime.now())),
        }).toISOTime({
          suppressMilliseconds: true,
        }) ?? "",
    });
  }
  return schedules;
};

export const CONTROL_IQ_RESPONSE = {
  value: 231,
  name: "Control-IQ Information",
  version: 105900,
  format: "bbbsb24B",
  fields: [
    "closed_loop_enabled",
    "hypo_light_alert_enabled",
    "total_daily_insulin",
    "weight",
    "weight_units",
    "sleep_schedule",
  ] as const,
  postprocess: rec => {
    return {
      closedLoopEnabled: !!rec.closed_loop_enabled,
      hypoLightAlertEnabled: !!rec.hypo_light_alert_enabled,
      totalDailyInsulin: rec.total_daily_insulin,
      weight: rec.weight_units === 2 ? rec.weight : rec.weight * 0.453592, // weight_units = 1 => Lbs / weight_units = 2 => Kg
      sleepSchedules: getSchedules(rec.sleep_schedule),
    };
  },
  schema: controlIQPayloadSchema,
} as const satisfies TandemResponse<ControlIQPayload, ControlIQData>;

export const CONTROL_IQ_COMMAND = {
  value: 230,
  name: "Control-IQ Information Request",
  version: 105900,
  response: CONTROL_IQ_RESPONSE,
} as const satisfies TandemCommand<ControlIQPayload, ControlIQData>;
